import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbTooltip, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { MotionMasterMessage } from 'motion-master-client';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { DeviceService } from '@app/services/device.service';
import { ModalService } from '@app/services/modal.service';
import { MonitoringService } from '@app/services/monitoring.service';
import { CoggingTorqueCompensationGraphComponent } from '@app/shared/cogging-torque-compensation-graph/cogging-torque-compensation-graph.component';
import { DeviceBaseComponent } from '@app/shared/device-base.component';
import { DocFeelingLuckyLinkComponent } from '@app/shared/doc-feeling-lucky-link/doc-feeling-lucky-link.component';
import { IfExpertModeDirective } from '@app/shared/if-expert-mode.directive';
import { IsPdoControlModeDirective } from '@app/shared/is-pdo-control-mode.directive';
import { LoadingComponent } from '@app/shared/loading/loading.component';
import { MotionPreventedTooltipDirective } from '@app/shared/motion-prevented-tooltip.directive';
import { DeviceParameter } from '@app/store/device-parameter.model';
import { Device } from '@app/store/device.model';
import { EncoderConfiguration } from '@app/store/encoder-configuration.model';
import { instanceOfSensorConfiguration, SensorConfiguration } from '@app/store/sensor-configuration.model';
import { getCurrentValue } from '@app/util';

@Component({
  selector: 'app-cogging-torque-compensation',
  templateUrl: './cogging-torque-compensation.component.html',
  imports: [
    LoadingComponent,
    DocFeelingLuckyLinkComponent,
    NgbTooltip,
    MotionPreventedTooltipDirective,
    IsPdoControlModeDirective,
    IfExpertModeDirective,
    FormsModule,
    NgbPopover,
    CoggingTorqueCompensationGraphComponent,
    AsyncPipe,
  ],
})
export class CoggingTorqueCompensationComponent extends DeviceBaseComponent implements OnInit, OnDestroy {
  private deviceService = inject(DeviceService);
  private monitoringService = inject(MonitoringService);
  protected modalService = inject(ModalService);

  data$: Observable<number[]>;
  dataValid$: Observable<boolean>;

  brakeActive$: Observable<boolean>;
  torqueControllerEnabled$: Observable<boolean>;
  coggingTorqueCompensationEnabledParameter$: Observable<DeviceParameter>;

  encoderConfigurationUsedForTorque$: Observable<EncoderConfiguration | SensorConfiguration>;

  recording$: Observable<boolean>;

  isMotionPrevented$: Observable<boolean>;

  positionsMonitoringSubscription = new Subscription();
  positions: { actual: number; single: number };

  skipAutoTuning: boolean;

  onDeviceChange(device: Device) {
    this.data$ = this.deviceService.selectDeviceCoggingTorqueDataInPermille(device);
    this.dataValid$ = this.data$.pipe(map((data) => (data ? data.length === 1024 : false)));

    this.brakeActive$ = this.deviceService.selectDeviceBrakeActive(device);
    this.torqueControllerEnabled$ = this.deviceService.selectDeviceTorqueControllerEnabled(device);
    this.coggingTorqueCompensationEnabledParameter$ = this.deviceService.selectDeviceParameter(
      device,
      'coggingTorqueCompensationEnabled',
    );

    this.encoderConfigurationUsedForTorque$ = this.deviceService.selectDeviceEncoderConfigurationUsedForTorque(device);

    this.recording$ = this.deviceService.selectDeviceCoggingTorqueRecordingRunning(device);

    this.isMotionPrevented$ = this.deviceService.selectIsMotionPrevented(device);

    this.positionsMonitoringSubscription.unsubscribe();
    const ec = getCurrentValue(this.encoderConfigurationUsedForTorque$);
    if (ec) {
      if (instanceOfSensorConfiguration(ec)) {
        this.positionsMonitoringSubscription = this.monitoringService
          .startMonitoringActualPositions(device.serialNumber, 32000)
          .subscribe(
            ([primary, secondary]: [number, number]) => (this.positions = ec.getPositions(primary, secondary)),
          );
      } else {
        this.positionsMonitoringSubscription = this.monitoringService
          .startMonitoringAdjustedPositions(device.deviceAddress, 40000)
          .subscribe(
            ([pos1, pos2]: [number, number]) =>
              (this.positions = ec.ordinal === 1 ? ec.getPositions(pos1) : ec.getPositions(pos2)),
          );
      }
    }
  }

  ngOnInit(): void {
    this.deviceService.requestLoadCoggingTorqueData(this.device);
  }

  ngOnDestroy() {
    this.onDisableTorqueController();
    this.positionsMonitoringSubscription.unsubscribe();
  }

  onStartRecording() {
    const pTimeout = this.skipAutoTuning ? 180000 : 300000;
    this.data$ = this.deviceService.selectDeviceCoggingTorqueDataInPermille(this.device);
    this.deviceService.requestStartCoggingTorqueRecording(this.device, this.skipAutoTuning, pTimeout);
  }

  onCancel() {
    this.deviceService.requestStopDevice(this.device);
  }

  onEnableTorqueController() {
    this.deviceService.requestEnableMotionController(
      this.device,
      MotionMasterMessage.Request.EnableMotionController.ControllerType.TORQUE,
    );
  }

  onDisableTorqueController() {
    this.deviceService.requestDisableMotionController(this.device);
  }

  onEnableCoggingTorqueCompensation() {
    this.deviceService.requestEnableCoggingTorqueCompensation(this.device);
  }

  onDisableCoggingTorqueCompensation() {
    this.deviceService.requestDisableCoggingTorqueCompensation(this.device);
  }

  onDeleteData() {
    const modalRef = this.modalService.openDeleteCoggingTorqueDataModal();
    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.deviceService.requestDeleteDeviceFile(this.device, 'cogging_torque.bin');
        this.data$ = null;
      }
    });
  }
}
